import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  EMPTY,
  catchError,
  concatMap,
  finalize,
  forkJoin,
  map,
  mergeMap,
  of,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs';

import { handleHttpErrorWithSnackbar } from 'src/app/shared/utils';
import { cropYearChanged } from 'src/app/store/actions';
import { blobToBase64 } from 'src/app/tga/shared/utils/blob.utils';

import {
  fetchGeneralAdvice,
  fetchGeneralAdviceFailure,
  fetchGeneralAdviceSuccess,
  fetchGeneralQuestions,
  fetchGeneralQuestionsFailure,
  fetchGeneralQuestionsSuccess,
  fetchGenericSoilQuestions,
  fetchGenericSoilQuestionsFailure,
  fetchGenericSoilQuestionsSuccess,
  fetchGenericGeneralQuestions,
  fetchGenericGeneralQuestionsFailure,
  fetchGenericGeneralQuestionsSuccess,
  fetchSoilAdvice,
  fetchSoilAdviceFailure,
  fetchSoilAdviceSuccess,
  fetchSoilQuestions,
  fetchSoilQuestionsFailure,
  fetchSoilQuestionsSuccess,
  fetchVarietyPropertiesByEdiCode,
  fetchVarietyPropertiesByEdiCodeFailure,
  fetchVarietyPropertiesByEdiCodeSuccess,
  loadCropFields,
  loadCropFieldsFailure,
  loadCropFieldsSuccess,
  loadTgaBiomassImage,
  loadTgaBiomassImageFailure,
  loadTgaBiomassImageSuccess,
  loadTgaBiomassPotatoImage,
  loadTgaBiomassPotatoImageFailure,
  loadTgaBiomassPotatoImageSuccess,
  loadTgaSoilImage,
  loadTgaSoilImageFailure,
  loadTgaSoilImageSuccess,
  postGeneralQuestions,
  postGeneralQuestionsFailure,
  postGeneralQuestionsSuccess,
  postGenericGeneralQuestions,
  postGenericGeneralQuestionsFailure,
  postGenericGeneralQuestionsSuccess,
  postSoilQuestions,
  postSoilQuestionsFailure,
  postSoilQuestionsSuccess,
  postGenericSoilQuestions,
  postGenericSoilQuestionsSuccess,
  postGenericSoilQuestionsFailure,
  fetchPlantingQuestions,
  fetchPlantingQuestionsFailure,
  fetchPlantingQuestionsSuccess,
  postPlantingQuestions,
  postPlantingQuestionsFailure,
  postPlantingQuestionsSuccess,
  fetchGenericPlantingQuestions,
  fetchGenericPlantingQuestionsSuccess,
  fetchGenericPlantingQuestionsFailure,
  postGenericPlantingQuestions,
  postGenericPlantingQuestionsSuccess,
  postGenericPlantingQuestionsFailure,
  fetchPlantingAdvice,
  fetchPlantingAdviceFailure,
  fetchPlantingAdviceSuccess,
  loadTgaHeightImage,
  loadTgaHeightImageFailure,
  loadTgaHeightImageSuccess,
  loadNotifications,
  loadNotificationsSuccess,
  loadNotificationsFailure,
  fetchCultivationAdvice,
  fetchCultivationAdviceSuccess,
  fetchCultivationAdviceFailure,
  cancelCultivationRequests,
  loadWaterAuthorityExceedances,
  loadWaterAuthorityExceedancesSuccess,
  loadWaterAuthorityExceedancesFailure,
  closeNotification,
  closeNotificationFailure,
  closeNotificationSuccess,
  loadSatelliteImages,
  loadSatelliteImagesSuccess,
  loadSatelliteImagesFailure,
  loadFieldObservationImages,
  loadFieldObservationImagesSuccess,
  loadFieldObservationImagesFailure,
} from './tga.actions';
import {
  FieldObservationObservedThreatsDto,
  FieldObservationSectionDto,
  GetTgaBiomassImageResponse,
  GetTgaBiomassPotatoImageResponse,
  NotificationCategory,
  ObservedThreatCategories,
} from '../shared/models/models';
import { DataPlatformApiService } from '../shared/services/data-platform-api.service';
import { TgaApiService } from '../shared/services/tga-api.service';
import { TgaSharedService } from '../shared/services/tga-shared.service';

@Injectable()
export class TgaEffects {
  private readonly actions$ = inject(Actions);
  private readonly tgaApiService = inject(TgaApiService);
  private readonly locale = inject(LocaleService);
  private readonly store = inject(Store);
  private readonly tgaSharedService = inject(TgaSharedService);
  private readonly dataPlatformApiService = inject(DataPlatformApiService);
  loadCropFields$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadCropFields),
      switchMap(({ payload }) =>
        this.tgaApiService.getTgaCropFields(payload.cropYear).pipe(
          map((response) =>
            loadCropFieldsSuccess({
              cropFields: response,
            }),
          ),
          catchError((error) => of(loadCropFieldsFailure({ error }))),
        ),
      ),
    );
  });

  cropYearChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cropYearChanged),
      concatMap(({ cropYear }) => of(loadCropFields({ cropYear }), loadNotifications({ cropYear }))),
    ),
  );

  loadWaterAuthorityExceedancesOnCropFieldsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCropFieldsSuccess),
      mergeMap(({ payload: { cropFields } }) => {
        const uniqueIds = Array.from(
          new Set(
            cropFields
              .map((cf) => (cf.cropField as { waterAuthorityId?: number })?.waterAuthorityId)
              .filter((id): id is number => id != null),
          ),
        );

        return uniqueIds.length > 0 ? of(loadWaterAuthorityExceedances(uniqueIds)) : EMPTY;
      }),
    ),
  );

  fetchGeneralQuestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchGeneralQuestions),
      concatMap(({ payload }) =>
        this.tgaApiService.getGeneralQuestionsValue(payload.cropFieldId, payload.cropYear).pipe(
          map((questions) => fetchGeneralQuestionsSuccess({ questions })),
          startWith(CoreActions.setLoading({ loading: true })),
          catchError((error) => of(fetchGeneralQuestionsFailure({ error }))),
          finalize(() => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
          }),
        ),
      ),
    );
  });

  postGeneralQuestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postGeneralQuestions),
      concatMap(({ payload }) =>
        this.tgaApiService.postGeneralQuestions(payload.answers).pipe(
          map(() => postGeneralQuestionsSuccess()),
          catchError((error) => of(postGeneralQuestionsFailure({ error }))),
        ),
      ),
    );
  });

  postGeneralQuestionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postGeneralQuestionsSuccess),
      switchMap(() =>
        of(
          NotificationActions.successSnackbar({
            message: this.locale.translate('TGA.Questionnaire.SuccessMessage'),
          }),
        ),
      ),
    ),
  );

  postGeneralQuestionsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postGeneralQuestionsFailure),
      mergeMap(({ payload }) =>
        handleHttpErrorWithSnackbar(
          payload.error,
          'TGA.Questionnaire.ErrorMessage',
          this.locale,
          'TGA.Validation.Backend',
        ),
      ),
    ),
  );

  fetchGenericGeneralQuestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchGenericGeneralQuestions),
      concatMap(({ payload }) =>
        this.tgaApiService.getGenericGeneralQuestionsValue(payload.cropYear).pipe(
          map((questions) => fetchGenericGeneralQuestionsSuccess({ questions })),
          startWith(CoreActions.setLoading({ loading: true })),
          catchError((error) => of(fetchGenericGeneralQuestionsFailure({ error }))),
          finalize(() => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
          }),
        ),
      ),
    );
  });

  postGenericGeneralQuestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postGenericGeneralQuestions),
      concatMap(({ payload }) =>
        this.tgaApiService.postGenericGeneralQuestions(payload.answers).pipe(
          map(() => postGenericGeneralQuestionsSuccess()),
          catchError((error) => of(postGenericGeneralQuestionsFailure({ error }))),
        ),
      ),
    );
  });

  postGenericGeneralQuestionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postGenericGeneralQuestionsSuccess),
      switchMap(() =>
        of(
          NotificationActions.successSnackbar({
            message: this.locale.translate('TGA.Questionnaire.SuccessMessage'),
          }),
        ),
      ),
    ),
  );

  postGenericGeneralQuestionsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postGenericGeneralQuestionsFailure),
      mergeMap(({ payload }) =>
        handleHttpErrorWithSnackbar(
          payload.error,
          'TGA.Questionnaire.ErrorMessage',
          this.locale,
          'TGA.Validation.Backend',
        ),
      ),
    ),
  );

  fetchSoilQuestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchSoilQuestions),
      concatMap(({ payload }) =>
        this.tgaApiService.getSoilQuestions(payload.cropFieldId, payload.cropYear).pipe(
          map((questions) => fetchSoilQuestionsSuccess({ questions })),
          startWith(CoreActions.setLoading({ loading: true })),
          catchError((error) => of(fetchSoilQuestionsFailure({ error }))),
          finalize(() => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
          }),
        ),
      ),
    );
  });

  postSoilQuestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postSoilQuestions),
      concatMap(({ payload }) =>
        this.tgaApiService.postSoilQuestions(payload.answers).pipe(
          map(() => postSoilQuestionsSuccess()),
          catchError((error) => of(postSoilQuestionsFailure({ error }))),
        ),
      ),
    );
  });

  postSoilQuestionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postSoilQuestionsSuccess),
      switchMap(() =>
        of(
          NotificationActions.successSnackbar({
            message: this.locale.translate('TGA.Questionnaire.SuccessMessage'),
          }),
        ),
      ),
    ),
  );

  postSoilQuestionsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postSoilQuestionsFailure),
      mergeMap(({ payload }) =>
        handleHttpErrorWithSnackbar(
          payload.error,
          'TGA.Questionnaire.ErrorMessage',
          this.locale,
          'TGA.Validation.Backend',
        ),
      ),
    ),
  );

  loadTgaSoilImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTgaSoilImage),
      switchMap((action) =>
        this.tgaApiService.getTgaSoilImage(action.payload).pipe(
          map((response) => {
            if ('imageData' in response) {
              return loadTgaSoilImageSuccess(response);
            } else {
              return loadTgaSoilImageFailure({ error: 'Failed to load soil image' });
            }
          }),
          catchError((error) => of(loadTgaSoilImageFailure({ error: error.message || 'Unknown error' }))),
        ),
      ),
    ),
  );

  loadTgaBiomassImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTgaBiomassImage),
      switchMap((action) =>
        this.tgaApiService.getTgaBiomassImage(action.payload).pipe(
          map((response: GetTgaBiomassImageResponse) => {
            if ('imageData' in response) {
              return loadTgaBiomassImageSuccess(response);
            } else {
              return loadTgaBiomassImageFailure({ error: 'Failed to load biomass image' });
            }
          }),
          catchError((error) => of(loadTgaBiomassImageFailure({ error: error.message || 'Unknown error' }))),
        ),
      ),
    ),
  );

  loadTgaBiomassPotatoImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTgaBiomassPotatoImage),
      switchMap((action) =>
        this.tgaApiService.getTgaBiomassPotatoImage(action.payload).pipe(
          map((response: GetTgaBiomassPotatoImageResponse) => {
            if ('imageData' in response) {
              return loadTgaBiomassPotatoImageSuccess(response);
            } else {
              return loadTgaBiomassPotatoImageFailure({ error: 'Failed to load biomass potato image' });
            }
          }),
          catchError((error) => of(loadTgaBiomassPotatoImageFailure({ error: error.message || 'Unknown error' }))),
        ),
      ),
    ),
  );

  fetchGeneralAdvice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchGeneralAdvice),
      switchMap(({ payload }) =>
        this.tgaApiService.getGeneralAdvice(payload.cropFieldId, payload.cropYear).pipe(
          map((generalAdvice) => {
            const displayAdvice = this.tgaSharedService.mapAdviceList(generalAdvice);
            return fetchGeneralAdviceSuccess({
              generalAdvice: displayAdvice,
              cropFieldId: payload.cropFieldId,
              cropYear: payload.cropYear,
              translationParams: {},
            });
          }),
          catchError((error) => of(fetchGeneralAdviceFailure({ error }))),
        ),
      ),
    ),
  );

  fetchVarietyPropertiesByEdiCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchVarietyPropertiesByEdiCode),
      switchMap(({ payload }) =>
        this.tgaApiService.getVarietyPropertiesByEdiCode(payload.ediCode).pipe(
          map((response) => {
            if ('id' in response) {
              return fetchVarietyPropertiesByEdiCodeSuccess(response);
            } else {
              return fetchVarietyPropertiesByEdiCodeFailure({ error: 'Failed to load variety properties' });
            }
          }),
          catchError((error) =>
            of(fetchVarietyPropertiesByEdiCodeFailure({ error: error.message || 'Unknown error' })),
          ),
        ),
      ),
    ),
  );

  fetchSoilAdvice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchSoilAdvice),
      concatMap(({ payload }) =>
        this.tgaApiService.getSoilAdvice(payload.cropFieldId, payload.cropYear).pipe(
          map((response) => {
            const displayAdvice = this.tgaSharedService.mapAdviceList(response.adviceLines);
            const transformedParams = this.tgaSharedService.transformTranslationParams(response.translationParams);
            return fetchSoilAdviceSuccess({
              soilAdvice: displayAdvice,
              cropFieldId: payload.cropFieldId,
              cropYear: payload.cropYear,
              translationParams: transformedParams,
            });
          }),
          catchError((error) => of(fetchSoilAdviceFailure({ error }))),
        ),
      ),
    ),
  );

  fetchGenericSoilQuestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchGenericSoilQuestions),
      concatMap(({ payload }) =>
        this.tgaApiService.getGenericSoilQuestions(payload.cropYear).pipe(
          map((questions) => fetchGenericSoilQuestionsSuccess({ questions })),
          startWith(CoreActions.setLoading({ loading: true })),
          catchError((error) => of(fetchGenericSoilQuestionsFailure({ error }))),
          finalize(() => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
          }),
        ),
      ),
    );
  });

  postGenericSoilQuestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postGenericSoilQuestions),
      concatMap(({ payload }) =>
        this.tgaApiService.postGenericSoilQuestions(payload.answers).pipe(
          map(() => postGenericSoilQuestionsSuccess()),
          catchError((error) => of(postGenericSoilQuestionsFailure({ error }))),
        ),
      ),
    );
  });

  postGenericSoilQuestionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postGenericSoilQuestionsSuccess),
      switchMap(() =>
        of(
          NotificationActions.successSnackbar({
            message: this.locale.translate('TGA.Questionnaire.SuccessMessage'),
          }),
        ),
      ),
    ),
  );

  postGenericSoilQuestionsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postGenericSoilQuestionsFailure),
      mergeMap(({ payload }) =>
        handleHttpErrorWithSnackbar(
          payload.error,
          'TGA.Questionnaire.ErrorMessage',
          this.locale,
          'TGA.Validation.Backend',
        ),
      ),
    ),
  );

  fetchPlantingQuestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchPlantingQuestions),
      concatMap(({ payload }) =>
        this.tgaApiService.getPlantingQuestions(payload.cropFieldId, payload.cropYear).pipe(
          map((questions) => fetchPlantingQuestionsSuccess({ questions })),
          startWith(CoreActions.setLoading({ loading: true })),
          catchError((error) => of(fetchPlantingQuestionsFailure({ error }))),
          finalize(() => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
          }),
        ),
      ),
    );
  });

  postPlantingQuestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postPlantingQuestions),
      concatMap(({ payload }) =>
        this.tgaApiService.postPlantingQuestions(payload.answers).pipe(
          map(() => postPlantingQuestionsSuccess()),
          catchError((error) => of(postPlantingQuestionsFailure({ error }))),
        ),
      ),
    );
  });

  postPlantingQuestionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postPlantingQuestionsSuccess),
      switchMap(() =>
        of(
          NotificationActions.successSnackbar({
            message: this.locale.translate('TGA.Questionnaire.SuccessMessage'),
          }),
        ),
      ),
    ),
  );

  postPlantingQuestionsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postPlantingQuestionsFailure),
      mergeMap(({ payload }) =>
        handleHttpErrorWithSnackbar(
          payload.error,
          'TGA.Questionnaire.ErrorMessage',
          this.locale,
          'TGA.Validation.Backend',
        ),
      ),
    ),
  );

  fetchGenericPlantingQuestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchGenericPlantingQuestions),
      concatMap(({ payload }) =>
        this.tgaApiService.getGenericPlantingQuestions(payload.cropYear).pipe(
          map((questions) => fetchGenericPlantingQuestionsSuccess({ questions })),
          startWith(CoreActions.setLoading({ loading: true })),
          catchError((error) => of(fetchGenericPlantingQuestionsFailure({ error }))),
          finalize(() => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
          }),
        ),
      ),
    );
  });

  postGenericPlantingQuestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postGenericPlantingQuestions),
      concatMap(({ payload }) =>
        this.tgaApiService.postGenericPlantingQuestions(payload.answers).pipe(
          map(() => postGenericPlantingQuestionsSuccess()),
          catchError((error) => of(postGenericPlantingQuestionsFailure({ error }))),
        ),
      ),
    );
  });

  postGenericPlantingQuestionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postGenericPlantingQuestionsSuccess),
      switchMap(() =>
        of(
          NotificationActions.successSnackbar({
            message: this.locale.translate('TGA.Questionnaire.SuccessMessage'),
          }),
        ),
      ),
    ),
  );

  postGenericPlantingQuestionsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postGenericPlantingQuestionsFailure),
      mergeMap(({ payload }) =>
        handleHttpErrorWithSnackbar(
          payload.error,
          'TGA.Questionnaire.ErrorMessage',
          this.locale,
          'TGA.Validation.Backend',
        ),
      ),
    ),
  );

  fetchPlantingAdvice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchPlantingAdvice),
      concatMap(({ payload }) =>
        this.tgaApiService.getPlantingAdvice(payload.cropFieldId, payload.cropYear).pipe(
          map((response) => {
            const displayAdvice = this.tgaSharedService.mapAdviceList(response.adviceLines);
            const transformedParams = this.tgaSharedService.transformTranslationParams(response.translationParams);
            return fetchPlantingAdviceSuccess({
              plantingAdvice: displayAdvice,
              cropFieldId: payload.cropFieldId,
              cropYear: payload.cropYear,
              translationParams: transformedParams,
            });
          }),
          catchError((error) => of(fetchPlantingAdviceFailure({ error }))),
        ),
      ),
    ),
  );

  loadTgaHeightImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTgaHeightImage),
      switchMap((action) =>
        this.tgaApiService.getTgaHeightImage(action.payload).pipe(
          map((response) => {
            if ('imageData' in response) {
              return loadTgaHeightImageSuccess(response);
            } else {
              return loadTgaHeightImageFailure({ error: 'Failed to load height image' });
            }
          }),
          catchError((error) => of(loadTgaHeightImageFailure({ error: error.message || 'Unknown error' }))),
        ),
      ),
    ),
  );

  loadNotifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadNotifications),
      switchMap(({ payload }) =>
        this.tgaApiService.getNotifications(payload.cropYear).pipe(
          map((response) => {
            if (Array.isArray(response)) {
              // Add category to existing warnings if not present (backward compatibility)
              const warningsWithTypeEnum = response.map((notification) => ({
                ...notification,
                category: notification.category ?? NotificationCategory.Warning,
              }));

              return loadNotificationsSuccess({ notifications: warningsWithTypeEnum });
            } else {
              return loadNotificationsFailure({ error: 'Failed to load notifications' });
            }
          }),
          catchError((error) => of(loadNotificationsFailure({ error: error.message || 'Unknown error' }))),
        ),
      ),
    );
  });
  closeNotification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(closeNotification),
      switchMap(({ payload }) =>
        this.tgaApiService.closeNotification(payload.notificationId).pipe(
          map(() => closeNotificationSuccess()),
          catchError((error) => {
            return of(closeNotificationFailure({ error }));
          }),
        ),
      ),
    );
  });
  fetchCultivationAdvice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchCultivationAdvice),
      switchMap(({ payload }) =>
        this.tgaApiService.getCultivationAdvice(payload.cropFieldId, payload.cropYear).pipe(
          takeUntil(this.actions$.pipe(ofType(cancelCultivationRequests))),
          map((response) => {
            const displayAdvice = this.tgaSharedService.mapAdviceList(response.adviceLines);
            const transformedParams = this.tgaSharedService.transformTranslationParams(response.translationParams);
            return fetchCultivationAdviceSuccess({
              cultivationAdvice: displayAdvice,
              cropFieldId: payload.cropFieldId,
              cropYear: payload.cropYear,
              translationParams: transformedParams,
            });
          }),
          catchError((error) => of(fetchCultivationAdviceFailure({ error }))),
        ),
      ),
    ),
  );

  loadWaterAuthorityExceedances$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadWaterAuthorityExceedances),
      concatMap(({ payload }) =>
        this.dataPlatformApiService.getWaterAuthorityExceedances(payload).pipe(
          map((results) => loadWaterAuthorityExceedancesSuccess({ results })),
          catchError((error) => of(loadWaterAuthorityExceedancesFailure({ error }))),
        ),
      ),
    ),
  );

  loadSatelliteImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSatelliteImages),
      switchMap(({ payload }) =>
        this.tgaApiService.getTgaSatelliteImageDates(payload.cropFieldId, payload.fromDate, payload.toDate).pipe(
          switchMap((dates) => {
            // Handle empty dates array
            if (!dates || dates.length === 0) {
              return of(loadSatelliteImagesSuccess([]));
            }
            const imageRequests = dates.map((dateObj) => {
              const isoDateString =
                typeof dateObj.imageDate === 'string' ? dateObj.imageDate : new Date(dateObj.imageDate).toISOString();

              return this.tgaApiService.getTgaSatelliteImage(payload.cropFieldId, isoDateString).pipe(
                switchMap((blob) => blobToBase64(blob)),
                map((base64) => ({
                  imageDate: isoDateString,
                  imageBase64: base64,
                  contentType: 'image/png',
                })),
                catchError((error) => {
                  console.error(`Failed to load satellite image for date ${isoDateString}:`, error);
                  return of(null); // Skip failed images
                }),
              );
            });

            return forkJoin(imageRequests).pipe(
              map((images) => images.filter((img) => img !== null)), // Remove null entries
              map((validImages) => loadSatelliteImagesSuccess(validImages)),
            );
          }),
          takeUntil(this.actions$.pipe(ofType(cancelCultivationRequests))),
          catchError((error) =>
            of(loadSatelliteImagesFailure({ error: error.message || 'Failed to load satellite images' })),
          ),
        ),
      ),
    ),
  );

  loadFieldObservationImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFieldObservationImages),
      switchMap(({ payload }) =>
        this.tgaApiService.getCropFieldObservations(payload.cropFieldId).pipe(
          switchMap((fieldObservations) => {
            if (!fieldObservations || fieldObservations.length === 0) {
              return of(loadFieldObservationImagesSuccess([]));
            }

            const imageRequests = fieldObservations.flatMap((observation) => {
              const observations = this.mapObservedThreatsToSections(observation.observedThreats);

              if (!observation.images || observation.images.length === 0) {
                return [
                  of({
                    observationId: observation.observationId,
                    description: observation.description,
                    createdAt: observation.createdAt,
                    imageCount: 0,
                    imageId: '',
                    imageBase64: '',
                    contentType: '',
                    observations,
                  }),
                ];
              }

              return observation.images.map((image) =>
                this.tgaApiService
                  .getFieldObservationImage(payload.cropFieldId, observation.observationId, image.imageId)
                  .pipe(
                    switchMap((blob) => blobToBase64(blob)),
                    map((base64) => ({
                      observationId: observation.observationId,
                      description: observation.description,
                      createdAt: observation.createdAt,
                      imageCount: observation.imageCount,
                      imageId: image.imageId,
                      imageBase64: base64,
                      contentType: 'image/png',
                      observations,
                    })),
                    catchError((error) => {
                      console.error(`Failed to load field observation image ${image.imageId}:`, error);
                      return of(null);
                    }),
                  ),
              );
            });

            return forkJoin(imageRequests).pipe(
              map((images) => images.filter((img) => img !== null)),
              map((validImages) => loadFieldObservationImagesSuccess(validImages)),
            );
          }),
          takeUntil(this.actions$.pipe(ofType(cancelCultivationRequests))),
          catchError((error) =>
            of(
              loadFieldObservationImagesFailure({
                error: error.message || this.locale.translate('TGA.Cultivation.FieldObservationImages.Error'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  private mapObservedThreatsToSections(
    observedThreats: FieldObservationObservedThreatsDto | undefined,
  ): FieldObservationSectionDto[] {
    if (!observedThreats) {
      return [];
    }

    const observedThreatSections = ObservedThreatCategories.flatMap((category) => {
      const values = observedThreats[category.key] ?? [];

      if (values.length === 0) {
        return [];
      }

      return [
        {
          category: category.categoryTranslationKey,
          items: values.map((value) => `${category.itemTranslationPrefix}.${value}`),
        },
      ];
    });

    return [...observedThreatSections, ...this.mapObservedThreatDetailsToSections(observedThreats)];
  }

  private mapObservedThreatDetailsToSections(
    observedThreats: FieldObservationObservedThreatsDto,
  ): FieldObservationSectionDto[] {
    const sections: FieldObservationSectionDto[] = [];

    if (observedThreats.bbch !== undefined && observedThreats.bbch !== null) {
      sections.push({
        category: 'TGA.Notifications.FieldObservationDialog.Fields.Bbch',
        items: [String(observedThreats.bbch)],
      });
    }

    if (observedThreats.cropPhase) {
      sections.push({
        category: 'TGA.Notifications.FieldObservationDialog.Fields.CropPhase',
        items: [`TGA.Notifications.FieldObservationDialog.CropPhase.${observedThreats.cropPhase}`],
      });
    }

    if (observedThreats.phytophthora?.length) {
      sections.push({
        category: 'TGA.Notifications.FieldObservationDialog.Fields.Phytophthora',
        items: observedThreats.phytophthora.map(
          (value) => `TGA.Notifications.FieldObservationDialog.Phytophthora.${value}`,
        ),
      });
    }

    return sections;
  }
}
